/*
 * Headquiet product page script (module, loaded by sections/hq-session.liquid and the tier price helper below).
 *   hq-dial      Session dial: pick a mode, red glow, music, run one session sped up (a demo, the button says so)
 *   hq-hotspots  pins on the photo and the list under it open the same item
 *   price on the Add to cart button follows the picked quantity tier (factory-qty-tiers)
 * Reduced motion and html.factory-lowfx: the run jumps to the end, the bars don't hum.
 */

const root = document.documentElement;
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.classList.contains('factory-lowfx');

const PATTERNS = [
  { bars: [0.8, 0.8, 0.8, 0.8, 0.8, 0.8, 0.8], d: 0.5, lag: () => 0 },
  { bars: [1, 0.3, 1, 0.3, 1, 0.3, 1], d: 0.28, lag: (i) => (i % 2) * 0.14 },
  { bars: [0.35, 0.55, 0.8, 1, 0.8, 0.55, 0.35], d: 0.9, lag: (i) => i * 0.12 },
  { bars: [0.5, 1, 0.5, 1, 0.5, 1, 0.5], d: 0.4, lag: (i) => i * 0.06 },
];

class HqDial extends HTMLElement {
  connectedCallback() {
    this.dial = this.querySelector('[data-dial]');
    this.bars = [...this.querySelectorAll('[data-dial-bars] i')];
    this.lit = [...this.querySelectorAll('.hq-dial__on')];
    this.hand = this.querySelector('.hq-dial__hand');
    this.timeEl = this.querySelector('[data-dial-time]');
    this.modeEl = this.querySelector('[data-dial-mode]');
    this.run = this.querySelector('[data-run]');
    this.runLabel = this.run?.textContent || '';
    this.minutes = Number(this.dataset.minutes) || 15;
    this.mode = 1;
    this.running = false;
    this.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => this.setMode(Number(b.dataset.mode))));
    const glow = this.querySelector('[data-glow-tgl]');
    glow?.addEventListener('click', () => {
      const on = glow.getAttribute('aria-pressed') !== 'true';
      glow.setAttribute('aria-pressed', String(on));
      this.dial.classList.toggle('is-glow', on);
    });
    const music = this.querySelector('[data-music-tgl]');
    music?.addEventListener('click', () => music.setAttribute('aria-pressed', String(music.getAttribute('aria-pressed') !== 'true')));
    this.run?.addEventListener('click', () => this.start());
    this.setMode(1);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
  }

  setMode(m) {
    this.mode = m;
    const button = this.querySelector(`[data-mode="${m}"]`);
    if (this.modeEl && button) this.modeEl.textContent = button.dataset.modeName;
    const pattern = PATTERNS[(m - 1) % PATTERNS.length];
    this.bars.forEach((bar, i) => {
      bar.style.setProperty('--hi', pattern.bars[i]);
      bar.style.setProperty('--lo', (pattern.bars[i] * 0.3).toFixed(2));
      bar.style.setProperty('--dd', `${pattern.d}s`);
      bar.style.setProperty('--dl', `${pattern.lag(i)}s`);
      bar.style.transform = this.running ? '' : `scaleY(${(pattern.bars[i] * 0.5).toFixed(2)})`;
    });
    this.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-checked', String(Number(b.dataset.mode) === m)));
  }

  setTime(f) {
    const total = this.minutes * 60;
    const left = Math.round((1 - f) * total);
    const shown = f >= 1 ? 0 : left;
    this.timeEl.textContent = `${Math.floor(shown / 60)}:${String(shown % 60).padStart(2, '0')}`;
    const n = Math.round(f * this.lit.length);
    this.lit.forEach((line, i) => line.classList.toggle('is-on', i < n));
    if (this.hand) this.hand.style.transform = `rotate(${(f * 360).toFixed(2)}deg)`;
  }

  finish() {
    this.running = false;
    this.dial.classList.remove('is-run');
    this.timeEl.textContent = this.dataset.off || 'Off';
    if (this.modeEl) this.modeEl.textContent = this.dataset.done || '';
    if (this.run) this.run.textContent = this.dataset.again || this.runLabel;
    this.bars.forEach((bar) => (bar.style.transform = 'scaleY(.12)'));
  }

  start() {
    cancelAnimationFrame(this.raf);
    if (still()) {
      this.setTime(1);
      this.finish();
      return;
    }
    this.running = true;
    this.dial.classList.add('is-run');
    this.setMode(this.mode);
    this.bars.forEach((bar) => (bar.style.transform = ''));
    if (this.run) this.run.textContent = this.dataset.running || this.runLabel;
    const t0 = performance.now();
    const duration = 6000;
    const step = (t) => {
      const f = Math.min(1, (t - t0) / duration);
      this.setTime(f);
      if (f < 1) this.raf = requestAnimationFrame(step);
      else this.finish();
    };
    this.raf = requestAnimationFrame(step);
  }
}
if (!customElements.get('hq-dial')) customElements.define('hq-dial', HqDial);

class HqHotspots extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const b = event.target.closest('[data-pin]');
      if (!b || !this.contains(b)) return;
      const id = b.dataset.pin;
      const open = b.getAttribute('aria-expanded') !== 'true';
      this.querySelectorAll('[data-pin]').forEach((x) => x.setAttribute('aria-expanded', String(open && x.dataset.pin === id)));
    });
  }
}
if (!customElements.get('hq-hotspots')) customElements.define('hq-hotspots', HqHotspots);

/* ------------------------------------------------------------------ tier price on the Add to cart button */
function tierPrice(scope) {
  const box = scope.querySelector('factory-qty-tiers input[type="radio"]:checked + .factory-tier__box .factory-tier__price');
  if (!box) return '';
  const node = [...box.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
  return node ? node.textContent.trim() : '';
}
function paintAtc(scope) {
  const price = tierPrice(scope);
  scope.querySelectorAll('product-form-component .add-to-cart-button').forEach((btn) => {
    if (btn.disabled) {
      btn.removeAttribute('data-hq-price');
      return;
    }
    if (price) btn.setAttribute('data-hq-price', price);
    else btn.removeAttribute('data-hq-price');
  });
}
function watchAtc() {
  document.querySelectorAll('factory-qty-tiers').forEach((tiers) => {
    const scope = tiers.closest('.shopify-section') || document;
    if (scope.dataset.hqAtc) return;
    scope.dataset.hqAtc = '1';
    paintAtc(scope);
    scope.addEventListener('change', () => requestAnimationFrame(() => paintAtc(scope)));
    new MutationObserver(() => paintAtc(scope)).observe(scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchAtc);
else watchAtc();
